// src/components/leave/ApproveLeaves.jsx - FIXED ALL FILTER ISSUE
import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../../styles/LeaveManagement.css';

const ApproveLeaves = ({ showDepartmentView = false, hideHeader = false }) => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [action, setAction] = useState(''); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadLeaves();
  }, [statusFilter]);

  const loadLeaves = async () => {
    try {
      setLoading(true);
      let data;

      if (showDepartmentView) {
        // For HOD dashboard - show all department leaves
        data = await leaveAPI.getDepartmentLeaves(statusFilter);
      } else {
        // For approval view - show only leaves pending my approval
        data = await leaveAPI.getPendingApprovals(statusFilter);
      }

      const leavesArray = data?.results || data || [];
      setLeaves(Array.isArray(leavesArray) ? leavesArray : []);
    } catch (error) {
      console.error('Error loading leaves:', error);
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (leave) => {
    setSelectedLeave(leave);
    setAction('approve');
    setRejectionReason('');
    setError('');
    setSuccess('');
    setShowApprovalModal(true);
  };

  const handleRejectClick = (leave) => {
    setSelectedLeave(leave);
    setAction('reject');
    setRejectionReason('');
    setError('');
    setSuccess('');
    setShowApprovalModal(true);
  };

  const handleSubmitApproval = async (e) => {
    e.preventDefault();

    if (action === 'reject' && !rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await leaveAPI.approveRejectLeave({
        leave_id: selectedLeave.id,
        action: action,
        rejection_reason: action === 'reject' ? rejectionReason : ''
      });

      setSuccess(`Leave ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowApprovalModal(false);
      setSelectedLeave(null);
      setRejectionReason('');

      // Reload leaves
      loadLeaves();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing leave:', err);
      setError(err.response?.data?.error || `Failed to ${action} leave`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'PENDING': 'badge-orange',
      'APPROVED': 'badge-green',
      'REJECTED': 'badge-red'
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  return (
    <div className="approve-leaves-container">
      {success && (
        <div className="alert alert-success">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      {!hideHeader ? (
        <div className="approve-leaves-header">
          <h2>{showDepartmentView ? 'Department Leave Requests' : 'Leaves Pending Your Approval'}</h2>
          <div className="filter-controls">
            <label htmlFor="status-filter">Filter:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="ALL">All Status</option>
            </select>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, gap: 10 }}>
          <label htmlFor="status-filter" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Filter Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)' }}
          >
            <option value="PENDING">Pending Requests</option>
            <option value="APPROVED">Approved Requests</option>
            <option value="REJECTED">Rejected Requests</option>
            <option value="ALL">All Requests</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading leave requests...</p>
        </div>
      ) : leaves.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No {statusFilter.toLowerCase()} leave requests</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Leave Type</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>Duration</th>
                <th>Reason</th>
                <th>Status</th>
                {/* FIXED: Show Actions column for PENDING filter OR ALL filter */}
                {(statusFilter === 'PENDING' || statusFilter === 'ALL') && <th>Actions</th>}
                {/* FIXED: Show Rejection Reason column for REJECTED filter OR ALL filter */}
                {(statusFilter === 'REJECTED' || statusFilter === 'ALL') && <th>Rejection Reason</th>}
                {/* FIXED: Show Approved By column for APPROVED filter OR ALL filter */}
                {(statusFilter === 'APPROVED' || statusFilter === 'ALL') && <th>Approved By</th>}
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave.id}>
                  <td>
                    <div>
                      <strong>{leave.applicant_name}</strong>
                      <br />
                      <small>{leave.applicant_employee_id}</small>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-blue">
                      {leave.applicant_subrole || leave.applicant_role}
                    </span>
                  </td>
                  <td><strong>{leave.leave_type_display}</strong></td>
                  <td>{formatDate(leave.from_date)}</td>
                  <td>{formatDate(leave.to_date)}</td>
                  <td>
                    <span className="duration-badge">{leave.duration_days} day{leave.duration_days > 1 ? 's' : ''}</span>
                  </td>
                  <td>
                    <div className="reason-cell" title={leave.reason}>
                      {leave.reason.length > 50 ? leave.reason.substring(0, 50) + '...' : leave.reason}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(leave.status)}>
                      {leave.status_display}
                    </span>
                  </td>
                  {/* FIXED: Show Actions based on filter AND individual leave status */}
                  {(statusFilter === 'PENDING' || statusFilter === 'ALL') && (
                    <td>
                      {leave.status === 'PENDING' ? (
                        <div className="action-buttons">
                          <button
                            className="btn-sm btn-success"
                            onClick={() => handleApproveClick(leave)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-sm btn-danger"
                            onClick={() => handleRejectClick(leave)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                  {/* FIXED: Show rejection reason based on filter AND individual leave status */}
                  {(statusFilter === 'REJECTED' || statusFilter === 'ALL') && (
                    <td>
                      {leave.status === 'REJECTED' ? (
                        <div className="rejection-reason-cell">
                          {leave.rejection_reason || 'No reason provided'}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                  {/* FIXED: Show approval info based on filter AND individual leave status */}
                  {(statusFilter === 'APPROVED' || statusFilter === 'ALL') && (
                    <td>
                      {leave.status === 'APPROVED' ? (
                        <div className="approval-info-cell">
                          {leave.approved_by_name || 'N/A'}
                          {leave.approved_at && (
                            <>
                              <br />
                              <small>{formatDate(leave.approved_at)}</small>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval/Rejection Modal */}
      {showApprovalModal && selectedLeave && (
        <div className="modal-overlay" onClick={() => !submitting && setShowApprovalModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{action === 'approve' ? 'Approve' : 'Reject'} Leave Request</h2>
              <button
                className="modal-close"
                onClick={() => !submitting && setShowApprovalModal(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitApproval}>
              <div className="modal-body">
                {error && <div className="error-message">{error}</div>}

                <div className="info-box">
                  <p><strong>Employee:</strong> {selectedLeave.applicant_name}</p>
                  <p><strong>Employee ID:</strong> {selectedLeave.applicant_employee_id}</p>
                  <p><strong>Role:</strong> {selectedLeave.applicant_subrole || selectedLeave.applicant_role}</p>
                  {selectedLeave.department_name && selectedLeave.department_name !== 'N/A' && !['DRIVER', 'TRANSPORT_MANAGER', 'FINANCE', 'HOSTEL_ADMIN', 'LIBRARY_STAFF'].includes(selectedLeave.applicant_role) && (
                    <p><strong>Department:</strong> {selectedLeave.department_name}</p>
                  )}
                  <p><strong>Leave Type:</strong> {selectedLeave.leave_type_display}</p>
                  <p><strong>Duration:</strong> {formatDate(selectedLeave.from_date)} - {formatDate(selectedLeave.to_date)} ({selectedLeave.duration_days} days)</p>
                  <p><strong>Reason:</strong> {selectedLeave.reason}</p>
                </div>

                {action === 'reject' && (
                  <div className="form-group">
                    <label htmlFor="rejection_reason">Rejection Reason *</label>
                    <textarea
                      id="rejection_reason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows="4"
                      placeholder="Provide a clear reason for rejecting this leave request"
                      required
                    />
                  </div>
                )}

                {action === 'approve' && (
                  <div className="warning-box">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Are you sure you want to approve this leave request?</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowApprovalModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={action === 'approve' ? 'btn-success' : 'btn-danger'}
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : action === 'approve' ? 'Approve Leave' : 'Reject Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveLeaves;